import { useCallback, useMemo } from 'react'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { generateRange } from '../../lib/utils'
import useId from '@mui/utils/useId'
import { isEndOfDay } from 'lib/time';
import { MomentRange, TimeRange } from './types'
import moment from 'moment'


type TimeSelectorProps = {
  currentAbsoluteValue: MomentRange
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>
}

type HourSelectorProps = {
  value: number
  setValue: (hour: number) => void
  label: string
  'aria-label': string
  isEnd?: boolean
}

function HourSelector({ value, setValue, label, 'aria-label': ariaLabel, isEnd }: HourSelectorProps) {
  const id = useId()
  const range = useMemo(() => isEnd ? [...generateRange(1, 24)] : [...generateRange(0, 23)], [isEnd])

  return (
    <TextField
      select
      value={value}
      onChange={e => setValue(Number(e.target.value))}
      label={label}
      id={id}
      sx={{ flex: '1' }}
      SelectProps={{ inputProps: { 'aria-label': ariaLabel } }}
    >
      {isEnd && <MenuItem disabled value="">--</MenuItem>}
      {range.map(option => (
        <MenuItem key={option} value={option}>
          {option === 24 ? 'Midnight' : moment(option, 'HH').format('h A')}
        </MenuItem>
      ))}
      {!isEnd && <MenuItem disabled value="">--</MenuItem>}
    </TextField>
  )
}

export default function TimeSelector({ currentAbsoluteValue, setTimeRange }: TimeSelectorProps) {
  const changeStartHour = useCallback((hour: number) => {
    const newValue = currentAbsoluteValue[0].clone().set('hour', hour)
    setTimeRange({ timeRange: [newValue, currentAbsoluteValue[1]] })
  }, [currentAbsoluteValue, setTimeRange])

  const changeEndHour = useCallback((hour: number) => {
    const newValue = hour === 24
      ? currentAbsoluteValue[1].clone().endOf('day')
      : currentAbsoluteValue[1].clone().set('hour', hour - 1).set('minute', 59)
    setTimeRange({ timeRange: [currentAbsoluteValue[0], newValue] })
  }, [currentAbsoluteValue, setTimeRange])

  return (
    <Stack direction="row" gap={2} sx={{ mx: 4 }}>
      <Box sx={{ flex: '2' }} />
      <HourSelector
        value={currentAbsoluteValue[0].hours()}
        setValue={changeStartHour}
        label={currentAbsoluteValue[0].format('MMM D')}
        aria-label="Start Time"
      />
      <HourSelector
        value={isEndOfDay(currentAbsoluteValue[1]) ? 24 : currentAbsoluteValue[1].clone().add(1, 'minute').hours() || 24}
        setValue={changeEndHour}
        label={currentAbsoluteValue[1].format('MMM D')}
        aria-label="End Time"
        isEnd
      />
    </Stack>
  )
}
